import { z } from "zod";

/**
 * `AJUSTER-TAUX` (the chef's « Ajuster le taux » window, WF-BNK-10 step 4): **`POST /api/v1/bank/decisions/{id}/preview`, the composer's own result at the rate the chef is
 * about to pick.** A preview, not a decision: it writes nothing, and a hard block is an answer (200) with the block listed, not a refusal. The words are the composer's stored ones
 * (`composer.ts`): the browser composes none of them, and prints the instalment, the TAEG and the sentences as they arrive.
 *
 * `justificationRequired` is true when the rate opens a gap that the standing offer does not carry: the same rule the decision door applies (`adjustedRateRefusal`).
 */

/** The one-decimal rate the decision door and its preview take: « 10 », « 9.8 », « 10.4 », never « 9.88 ». */
export const RATE_PCT_PATTERN = /^\d{1,2}(?:\.\d)?$/u;

export const decisionPreviewRequestSchema = z.strictObject({
  ratePct: z.string().regex(RATE_PCT_PATTERN),
});

/** A hard block of the composer (P1-LND-026): neither can be justified away. */
export const decisionPreviewBlockSchema = z.strictObject({
  code: z.enum(["taegAboveCeiling", "instalmentAboveCapacity"]),
  rule: z.string(),
  detail: z.string(),
});

/** A gap against the bank's policy (P1-LND-029), with the level that decides it. */
export const decisionPreviewGapSchema = z.strictObject({
  code: z.enum([
    "rateBelowGrid",
    "gridRateMissing",
    "durationAboveMax",
    "vehicleAgeAtMaturity",
    "ltvAboveMax",
  ]),
  rule: z.string(),
  detail: z.string(),
  level: z.enum(["chefCredit", "comiteCredit"]),
});

export const decisionPreviewSchema = z.strictObject({
  /** The monthly instalment at that rate, whole FCFA. */
  instalmentAmount: z.number().int(),
  /** The TAEG at that rate, one decimal (« 11.4 »). */
  taegPct: z.string(),
  blocks: z.array(decisionPreviewBlockSchema),
  gaps: z.array(decisionPreviewGapSchema),
  justificationRequired: z.boolean(),
});

export type DecisionPreview = z.infer<typeof decisionPreviewSchema>;
